import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { PhotoSlot } from "@shyne/shared/api";
import { getDistinctWorkflowDescription } from "@/lib/utils/workflow-photo-gate";
import { AppointmentWorkflow } from "@/models/appointment-task.model";
import { AppointmentClockData, AppointmentExtraPhoto } from "@/models/appointment.model";
import { Upload, Camera, CheckCircle2, X, Image as ImageIcon, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ImageLightbox } from "@/components/shared/image-lightbox";
import moment from "moment";

interface PhotoUploadSectionProps {
  serviceWorkflows: PhotoSlot[];
  appointmentWorkflows: AppointmentWorkflow[];
  onPhotoUpload: (workflowId: string, file: File, isAfter: boolean) => void;
  readOnly?: boolean;
  clockInData?: AppointmentClockData;
  clockOutData?: AppointmentClockData;
  // Ad-hoc worker photos beyond the required slots (display-only here)
  extraPhotos?: AppointmentExtraPhoto[];
}

export function PhotoUploadSection({
  serviceWorkflows,
  appointmentWorkflows,
  onPhotoUpload,
  readOnly = false,
  clockInData,
  clockOutData,
  extraPhotos = [],
}: PhotoUploadSectionProps) {
  const [expandedWorkflows, setExpandedWorkflows] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState<Set<string>>(new Set());
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string>("");
  const [lightboxAlt, setLightboxAlt] = useState<string>("");
  const fileInputRefs = useRef<{ [key: string]: { before: HTMLInputElement | null; after: HTMLInputElement | null } }>(
    {}
  );

  // Expand all workflows by default
  useEffect(() => {
    if (serviceWorkflows.length > 0) {
      const allIds = new Set(serviceWorkflows.map((wf) => wf.id));
      if (clockInData?.photoUrl) allIds.add("__clock_in__");
      if (clockOutData?.photoUrl) allIds.add("__clock_out__");
      setExpandedWorkflows(allIds);
    }
  }, [serviceWorkflows, clockInData, clockOutData]);

  const openLightbox = (imageUrl: string, alt: string) => {
    setLightboxImage(imageUrl);
    setLightboxAlt(alt);
    setLightboxOpen(true);
  };

  const toggleWorkflow = (workflowId: string) => {
    const newExpanded = new Set(expandedWorkflows);
    if (newExpanded.has(workflowId)) {
      newExpanded.delete(workflowId);
    } else {
      newExpanded.add(workflowId);
    }
    setExpandedWorkflows(newExpanded);
  };

  const getWorkflowStatus = (workflowId: string) => {
    const beforePhoto = appointmentWorkflows.find((aw) => aw.workflow === workflowId && !aw.is_after);
    const afterPhoto = appointmentWorkflows.find((aw) => aw.workflow === workflowId && aw.is_after);

    return { beforePhoto, afterPhoto };
  };

  const handleFileSelect = (workflowId: string, isAfter: boolean, event: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;

    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert("File size must be less than 10MB");
      return;
    }

    const key = `${workflowId}-${isAfter ? "after" : "before"}`;
    setUploading((prev) => new Set(prev).add(key));
    event.target.value = "";
    Promise.resolve(onPhotoUpload(workflowId, file, isAfter)).finally(() => {
      setUploading((prev) => {
        const newSet = new Set(prev);
        newSet.delete(key);
        return newSet;
      });
    });
  };

  const triggerFileInput = (workflowId: string, isAfter: boolean) => {
    if (readOnly) return;
    const key = isAfter ? "after" : "before";
    fileInputRefs.current[workflowId]?.[key]?.click();
  };

  const getUploadButtonText = (workflowId: string, isAfter: boolean) => {
    const uploadingKey = `${workflowId}-${isAfter ? "after" : "before"}`;
    if (uploading.has(uploadingKey)) {
      return "Uploading...";
    }
    return isAfter ? "Upload After Photo" : "Upload Before Photo";
  };

  const formatPhotoTime = (dateString: string) => {
    return moment(dateString).format("MM/DD/YYYY, h:mm a");
  };

  if (serviceWorkflows.length === 0 && !clockInData?.photoUrl && !clockOutData?.photoUrl) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <Camera className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No photo requirements defined for this service.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Photo Requirements Overview */}
      <div className="bg-muted/50 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-medium">Photo Documentation Progress</h3>
          <Badge variant="secondary">
            {appointmentWorkflows.length} of {serviceWorkflows.length * 2} photos uploaded
          </Badge>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div
            className="bg-primary h-2 rounded-full transition-all duration-300"
            style={{
              width:
                serviceWorkflows.length > 0
                  ? `${(appointmentWorkflows.length / (serviceWorkflows.length * 2)) * 100}%`
                  : "0%",
            }}
          />
        </div>
      </div>

      {/* Start Work Photo */}
      {clockInData?.photoUrl && (
        <Card className="overflow-hidden">
          <CardHeader
            className="cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => toggleWorkflow("__clock_in__")}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4 text-green-600" />
                Start Work Photo
              </CardTitle>
              <Badge variant="default">
                {moment(clockInData.time).format("h:mm a")}
              </Badge>
            </div>
          </CardHeader>
          {expandedWorkflows.has("__clock_in__") && (
            <CardContent className="pt-0">
              <div className="space-y-2">
                <div className="flex items-center justify-center">
                  <img
                    src={clockInData.photoUrl}
                    alt="Start work photo"
                    className="max-w-full max-h-48 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => openLightbox(clockInData.photoUrl, "Start work photo")}
                  />
                </div>
                <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatPhotoTime(clockInData.time)}
                </p>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* Photo Workflows */}
      <div className="space-y-3">
        {serviceWorkflows.map((workflow) => {
          const isExpanded = expandedWorkflows.has(workflow.id);
          const { beforePhoto, afterPhoto } = getWorkflowStatus(workflow.id);
          const hasBeforePhoto = !!beforePhoto;
          const hasAfterPhoto = !!afterPhoto;
          const workflowDescription = getDistinctWorkflowDescription(workflow);

          return (
            <Card key={workflow.id} className="overflow-hidden">
              <CardHeader
                className="cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => toggleWorkflow(workflow.id)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Camera className="w-4 h-4 text-primary" />
                    {workflow.title}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Badge variant={hasBeforePhoto ? "default" : "secondary"}>
                      Before {hasBeforePhoto ? "✓" : "○"}
                    </Badge>
                    <Badge variant={hasAfterPhoto ? "default" : "secondary"}>After {hasAfterPhoto ? "✓" : "○"}</Badge>
                  </div>
                </div>
                {workflowDescription && <p className="text-sm text-muted-foreground mt-1">{workflowDescription}</p>}
              </CardHeader>

              {isExpanded && (
                <CardContent className="pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Before Photo */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Before Photo</Label>
                      <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center">
                        {beforePhoto ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-center">
                              <CheckCircle2 className="w-8 h-8 text-green-500" />
                            </div>
                            <p className="text-sm text-green-600 font-medium">Photo uploaded</p>
                            <div className="flex items-center justify-center">
                              <img
                                src={beforePhoto.image}
                                alt="Before photo"
                                className="max-w-full max-h-32 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => openLightbox(beforePhoto.image, "Before photo")}
                              />
                            </div>
                            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatPhotoTime(beforePhoto.created_at)}
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <ImageIcon className="w-8 h-8 mx-auto text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">No before photo uploaded</p>
                          </div>
                        )}
                        <Input
                          ref={(el) => {
                            if (!fileInputRefs.current[workflow.id]) {
                              fileInputRefs.current[workflow.id] = { before: null, after: null };
                            }
                            fileInputRefs.current[workflow.id].before = el;
                          }}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileSelect(workflow.id, false, e)}
                        />
                      </div>
                    </div>

                    {/* After Photo */}
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">After Photo</Label>
                      <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-4 text-center">
                        {afterPhoto ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-center">
                              <CheckCircle2 className="w-8 h-8 text-green-500" />
                            </div>
                            <p className="text-sm text-green-600 font-medium">Photo uploaded</p>
                            <div className="flex items-center justify-center">
                              <img
                                src={afterPhoto.image}
                                alt="After photo"
                                className="max-w-full max-h-32 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => openLightbox(afterPhoto.image, "After photo")}
                              />
                            </div>
                            <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatPhotoTime(afterPhoto.created_at)}
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <ImageIcon className="w-8 h-8 mx-auto text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">No after photo uploaded</p>
                          </div>
                        )}
                        <Input
                          ref={(el) => {
                            if (!fileInputRefs.current[workflow.id]) {
                              fileInputRefs.current[workflow.id] = { before: null, after: null };
                            }
                            fileInputRefs.current[workflow.id].after = el;
                          }}
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleFileSelect(workflow.id, true, e)}
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Additional worker photos beyond the required slots */}
      {extraPhotos.length > 0 && (
        <Card className="overflow-hidden">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-primary" />
              Additional Photos
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {[false, true].map((isAfter) => {
              const photos = extraPhotos.filter((photo) => photo.is_after === isAfter);
              if (photos.length === 0) return null;
              const kind = isAfter ? "After" : "Before";

              return (
                <div key={kind} className="space-y-2">
                  <Label className="text-sm font-medium">Additional {kind} Photos</Label>
                  <div className="flex flex-wrap gap-3">
                    {photos.map((photo) => (
                      <img
                        key={photo.url}
                        src={photo.url}
                        alt={`Additional ${kind.toLowerCase()} photo`}
                        className="w-24 h-24 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => openLightbox(photo.url, `Additional ${kind.toLowerCase()} photo`)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Complete Work Photo */}
      {clockOutData?.photoUrl && (
        <Card className="overflow-hidden">
          <CardHeader
            className="cursor-pointer hover:bg-muted/50 transition-colors"
            onClick={() => toggleWorkflow("__clock_out__")}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-4 h-4 text-red-600" />
                Complete Work Photo
              </CardTitle>
              <Badge variant="default">
                {moment(clockOutData.time).format("h:mm a")}
              </Badge>
            </div>
          </CardHeader>
          {expandedWorkflows.has("__clock_out__") && (
            <CardContent className="pt-0">
              <div className="space-y-2">
                <div className="flex items-center justify-center">
                  <img
                    src={clockOutData.photoUrl}
                    alt="Completion photo"
                    className="max-w-full max-h-48 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                    onClick={() => openLightbox(clockOutData.photoUrl, "Completion photo")}
                  />
                </div>
                <p className="text-xs text-muted-foreground text-center flex items-center justify-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatPhotoTime(clockOutData.time)}
                </p>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        imageUrl={lightboxImage}
        alt={lightboxAlt}
      />
    </div>
  );
}
